import { useParams } from 'react-router-dom'
import CustomPaper from '../../containers/Paper'
import UserBasicInfo from './UserBasicInfo'
import { useEffect, useState } from 'react'
import { UserDTO } from '../../models/users'
import UserService from '../../services/UserService'
import RegisterProvider from '../../context/RegisterContext'
import {
    UserEducationalExperienceCard,
    UserProfessionalExperienceCard,
} from './UserExperienceCards'
import { isCurrentUser, sortDates } from '../../helpers/utils'
import PostService from '../../services/PostService'
import { PostDTO } from '../../models/post'
import Post from '../home/Post'
import CustomModal from '../../containers/Modal'
import PostModal from '../../components/Modal/PostModal'
import CustomButton from '../../components/Button'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import NoResults from '../../containers/NoResults'
import { Chip, CircularProgress } from '@mui/material'
import UtilitiesService from '../../services/UtilitiesService'
import { AppConfig } from '../../models/shared'
import CommunitiesService from '../../services/CommunitiesService'
import { CommunityDTO } from '../../models/community'
import CommunityCard from '../communities/CommunityCard'
import CommunityForm from '../communities/CommunityForm'

const UserPage = () => {
    const { userId } = useParams<{ userId: string }>()
    const [user, setUser] = useState<UserDTO>()
    const [self, setSelf] = useState<boolean>(false)
    const [posts, setPosts] = useState<PostDTO[]>()
    const [isNewPostOpen, setIsNewPostOpen] = useState<boolean>(false)
    const [mutate, setMutate] = useState<boolean>(false)
    const [config, setConfig] = useState<AppConfig>()
    const [communities, setCommunities] = useState<CommunityDTO[]>()
    const [mutateCommunity, setMutateCommunity] = useState<boolean>(false)
    const [isCommunityFormOpen, setIsCommunityFormOpen] = useState<boolean>(false)

    useEffect(() => {
        const userService = new UserService()
        userService.getUserById(userId!).then((response) => {
            if (response.status !== 200) return
            setUser(response.data)
        })
        userService.getCurrentUser().then((response) => {
            if (response.status !== 200) return
            setSelf((response.data as UserDTO).id === userId)
        })
        new UtilitiesService().getAppConfig().then((response) => {
            if (response.status !== 200) return
            setConfig(response.data)
        })
    }, [userId])

    useEffect(() => {
        new PostService().getPublishedPostsByUser(userId as string).then((response) => {
            if (response.status === 200) setPosts(response.data)
            else if (response.status === 404) setPosts([])
        })
    }, [userId, mutate])

    useEffect(() => {
        new CommunitiesService().getCommunitiesByUser(userId as string).then((response) => {
            if (response.status !== 200) return
            setCommunities(response.data)
        })
    }, [userId, mutateCommunity])

    const userIsMember = (community: CommunityDTO) =>
        community.members.some((user) => isCurrentUser(user))

    return (
        <RegisterProvider>
            <div className="w-full">
                {user ? (
                    <div className="flex flex-col gap-4 w-full text-secondary">
                        <UserBasicInfo
                            user={user}
                            self={self}
                            config={config}
                        />
                        <CustomPaper className="p-3 md:p-5">
                            <span className="text-lg md:text-2xl font-semibold text-primary">
                                Experiencia laboral
                            </span>
                            <div className="flex flex-col gap-2 md:gap-5 mt-2">
                                {user.workHistories
                                    .sort((x, y) => sortDates(x.endDate, y.endDate))
                                    .map((info) => (
                                        <UserProfessionalExperienceCard
                                            info={info}
                                            key={`work${info.startDate}`}
                                        />
                                    ))}
                            </div>
                        </CustomPaper>
                        <CustomPaper className="p-3 md:p-5">
                            <span className="text-lg md:text-2xl font-semibold text-primary">
                                Educación
                            </span>
                            <div className="flex flex-col gap-2 md:gap-5 mt-2">
                                {user.educationalHistories
                                    .sort((x, y) => sortDates(x.endDate, y.endDate))
                                    .map((info) => (
                                        <UserEducationalExperienceCard
                                            info={info}
                                            key={`education${info.startDate}`}
                                        />
                                    ))}
                            </div>
                        </CustomPaper>
                        <CustomPaper>
                            <span className="text-lg md:text-2xl font-semibold text-primary">
                                Aptitudes
                            </span>
                            <div className={`flex gap-1 items-center flex-wrap mt-1 md:mt-3`}>
                                {user.allAbilities.map((ability) => (
                                    <div key={`selected${ability}`}>
                                        <Chip
                                            label={ability}
                                            className="bg-optional text-secondary font-[500]"
                                            sx={{
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'white',
                                                },
                                                '&:hover .MuiChip-deleteIcon': {
                                                    color: 'white',
                                                },
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </CustomPaper>
                        <div className="flex gap-3 md:gap-6 md:mt-2">
                            <span className="text-xl md:text-3xl font-semibold">Comunidades</span>
                            {isCurrentUser(user) && (
                                <CustomButton
                                    text={`Crear Comunidad`}
                                    className="h-[36px]"
                                    onClick={() => setIsCommunityFormOpen(true)}
                                    startIcon={<AddCircleOutlineIcon />}
                                    secondary
                                />
                            )}
                        </div>
                        {communities ? (
                            <div
                                className={`grid ${communities.length > 0 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'} gap-2 md:gap-4`}
                            >
                                {communities.length > 0 ? (
                                    communities?.map((community) => (
                                        <CommunityCard
                                            community={community}
                                            userIsMember={userIsMember(community)}
                                            mutate={mutate}
                                            setMutate={setMutateCommunity}
                                            request={false}
                                        />
                                    ))
                                ) : (
                                    <CustomPaper>
                                        <NoResults
                                            title={
                                                isCurrentUser(user)
                                                    ? 'No pertenecés a ninguna comunidad'
                                                    : 'El usuario no es miembro de ninguna comunidad'
                                            }
                                            subtitle={
                                                isCurrentUser(user)
                                                    ? '¡Podés crear una desde la sección de comunidades!'
                                                    : 'Acá aparecerán todas las comunidades a las que pertenece'
                                            }
                                        />
                                    </CustomPaper>
                                )}
                            </div>
                        ) : (
                            <div className="flex w-full justify-center">
                                <CircularProgress
                                    className="m-2 md:m-5 text-primary"
                                    size={64}
                                />
                            </div>
                        )}
                        <div className="flex gap-3 md:gap-6 md:mt-2 items-center">
                            <span className="text-xl md:text-3xl font-semibold">Publicaciones</span>
                            {isCurrentUser(user) && (
                                <CustomButton
                                    text={`Crear Publicación`}
                                    className="h-[36px]"
                                    onClick={() => setIsNewPostOpen(true)}
                                    startIcon={<AddCircleOutlineIcon />}
                                    secondary
                                />
                            )}
                        </div>
                        {posts ? (
                            <div className="text-black flex flex-col gap-2 md:gap-4">
                                {posts.length > 0 ? (
                                    posts.map((post) => (
                                        <CustomPaper key={post.id}>
                                            <Post
                                                info={post}
                                                setMutate={setMutate}
                                            />
                                        </CustomPaper>
                                    ))
                                ) : (
                                    <CustomPaper>
                                        <NoResults
                                            title={
                                                isCurrentUser(user)
                                                    ? 'No tenés publicaciones aún'
                                                    : 'El usuario no tiene publicaciones'
                                            }
                                            subtitle={
                                                isCurrentUser(user)
                                                    ? '¡Pulsá el botón para crear tu primera publicación!'
                                                    : 'Acá aparecerán todas sus publicaciones'
                                            }
                                        />
                                    </CustomPaper>
                                )}
                            </div>
                        ) : (
                            <CircularProgress
                                className="m-5 text-primary flex w-full justify-center"
                                size={64}
                            />
                        )}
                    </div>
                ) : (
                    <CircularProgress
                        className="m-5 text-primary"
                        size={64}
                    />
                )}
                <CustomModal
                    isOpen={isNewPostOpen}
                    handleClose={() => setIsNewPostOpen(false)}
                >
                    <PostModal handleClose={() => setIsNewPostOpen(false)} />
                </CustomModal>
                <CustomModal
                    isOpen={isCommunityFormOpen}
                    handleClose={() => setIsCommunityFormOpen(false)}
                >
                    <CommunityForm
                        handleClose={() => setIsCommunityFormOpen(false)}
                        mutate={mutate}
                        setMutate={setMutateCommunity}
                    />
                </CustomModal>
            </div>
        </RegisterProvider>
    )
}

export default UserPage
